import { Fragment } from "react";

import { Collapse, Divider, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";

import { ArrowDownIcon, ArrowUpIcon } from "../../Icons/Icons";
import { MODULE_BROKER } from "../config/constants";
import { isMenuItemActive, isParentMenuItemActive } from "../helpers";

const SubMenuItem = ({
  sx,
  item,
  pathname,
  handleNavigate,
  calculateIsModuleAllowed,
  isBrokerMenuAllowedCheck, // Specific check for Broker subitems
  globalState,
}) => {
  if (item.condition && !item.condition(globalState)) {
    return null;
  }

  const path = typeof item.path === "function" ? item.path(globalState) : item.path;
  const isAllowed =
    item.allowedModule === null
      ? isBrokerMenuAllowedCheck() // Use specific broker logic if allowedModule is null
      : item.allowedModule === true || calculateIsModuleAllowed(item.allowedModule);
  const isDisabled = item.disabledCheck ? item.disabledCheck(globalState) : !isAllowed;

  return (
    <ListItemButton
      sx={{
        pl: 2,
        ...sx,
        color: isMenuItemActive(pathname, path) ? "secondary.main" : "inherit",
        borderRightStyle: "solid",
        borderRightWidth: "6px",
        borderRightColor: isMenuItemActive(pathname, path) ? "secondary.main" : "transparent",
      }}
      className={isMenuItemActive(pathname, path) ? "active" : ""}
      onClick={() => handleNavigate(path)}
      disabled={isDisabled || (item.disabledCheck && item.disabledCheck(globalState))}
    >
      <ListItemText primary={item.text} />
    </ListItemButton>
  );
};

const MenuItem = ({
  item,
  pathname,
  handleNavigate,
  calculateIsModuleAllowed,
  calculateIsSubMenuOpen,
  toggleIsSubMenuOpen,
  globalState,
}) => {
  if (item.condition && !item.condition(globalState)) {
    return null;
  }

  const path = typeof item.path === "function" ? item.path(globalState) : item.path;
  const IconComponent = item.icon;
  const hasChildren = item.children && item.children.length > 0;
  const isSubMenuOpen = hasChildren && calculateIsSubMenuOpen(item.id);
  const isParentActive = hasChildren
    ? !isSubMenuOpen && isParentMenuItemActive(pathname, item, globalState)
    : isMenuItemActive(pathname, path);

  const isBrokerLogic = item.id === MODULE_BROKER;
  const isBrokerMenuAllowedCheck = isBrokerLogic ? () => item.isBrokerMenuAllowed(globalState) : () => true;

  const isAllowed =
    item.allowedModule === true
      ? true
      : item.allowedModule === null
        ? true // Broker main item always visible if condition met, subitems checked separately
        : calculateIsModuleAllowed(item.allowedModule);

  const isDisabled = item.disabledCheck ? item.disabledCheck(globalState) : !isAllowed;

  const handleClick = () => {
    if (hasChildren) {
      toggleIsSubMenuOpen(item.id);
    } else if (path) {
      handleNavigate(path);
    }
  };

  const isCollapseOpen = isBrokerLogic
    ? isSubMenuOpen || (globalState.is_broker && !globalState?.producer_token)
    : isSubMenuOpen;

  return (
    <Fragment key={item.id}>
      <ListItemButton
        sx={{
          minHeight: "48px",
          color: isParentActive ? "secondary.main" : "inherit",
          borderRightStyle: "solid",
          borderRightWidth: "6px",
          borderRightColor: isParentActive ? "secondary.main" : "transparent",
          backgroundColor: isSubMenuOpen ? "action.hover" : "inherit",
          pl: 1.5,
        }}
        className={isParentActive ? "active" : ""}
        onClick={handleClick}
        disabled={isDisabled}
      >
        {IconComponent && (
          <ListItemIcon sx={{ mr: 1, minWidth: 32 }}>
            <IconComponent color={isParentActive && "secondary"} />
          </ListItemIcon>
        )}
        <ListItemText sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }} primary={item.text} />
        {hasChildren &&
          (isSubMenuOpen ? (
            <ArrowUpIcon sx={{ width: 18, height: 18 }} />
          ) : (
            <ArrowDownIcon sx={{ width: 18, height: 18 }} />
          ))}
      </ListItemButton>
      {isCollapseOpen && <Divider />}
      {hasChildren && (
        <Collapse in={isCollapseOpen} timeout="auto" unmountOnExit>
          <List component="div" sx={{ backgroundColor: isSubMenuOpen ? "action.hover" : "inherit" }} disablePadding>
            {item.children.map((subItem) => (
              <SubMenuItem
                sx={{
                  pl: 6.5,
                  height: "48px",
                }}
                key={subItem.id}
                item={subItem}
                pathname={pathname}
                handleNavigate={handleNavigate}
                calculateIsModuleAllowed={calculateIsModuleAllowed}
                isBrokerMenuAllowedCheck={isBrokerMenuAllowedCheck} // Pass broker check down
                globalState={globalState} // Pass globalState
              />
            ))}
          </List>
        </Collapse>
      )}
    </Fragment>
  );
};

export const MenuItemsRenderer = ({
  config,
  membersItem,
  pathname,
  handleNavigate,
  calculateIsModuleAllowed,
  calculateIsSubMenuOpen,
  toggleIsSubMenuOpen,
  globalState,
}) => {
  return (
    <>
      <List>
        {config.map((item) => (
          <MenuItem
            key={item.id}
            item={item}
            pathname={pathname}
            handleNavigate={handleNavigate}
            calculateIsModuleAllowed={calculateIsModuleAllowed}
            calculateIsSubMenuOpen={calculateIsSubMenuOpen}
            toggleIsSubMenuOpen={toggleIsSubMenuOpen}
            globalState={globalState}
          />
        ))}
      </List>
      {membersItem && membersItem.condition(globalState) && (
        <List>
          <MenuItem
            item={membersItem}
            pathname={pathname}
            handleNavigate={handleNavigate}
            calculateIsModuleAllowed={() => true}
            calculateIsSubMenuOpen={() => false}
            toggleIsSubMenuOpen={() => {}}
            globalState={globalState}
          />
        </List>
      )}
    </>
  );
};
